import React, { useState, useEffect } from 'react';
import { useAdminAuth } from '../auth/useAdminAuth';
import { useAdminData } from '../components/shared/useAdminData';
import DataTable from '../components/common/DataTable';
import Modal from '../components/common/Modal';
import SearchBar from '../components/common/SearchBar';
import StatusBadge from '../components/common/StatusBadge';
import SkeletonLoaderComponents from '../components/shared/SkeletonLoader';
import { API_ENDPOINTS } from '../config/api-endpoints';
import './SupportTickets.css';

const { Skeleton } = SkeletonLoaderComponents;

const SupportTickets = () => {
  const { user } = useAdminAuth();
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [filters, setFilters] = useState({
    status: 'all',
    priority: 'all',
    category: 'all',
    assignee: 'all',
    search: ''
  });

  // Fetch tickets with filters
  const { data: ticketsData, loading, error, refetch } = useAdminData(
    'support-tickets',
    API_ENDPOINTS.ADMIN.SUPPORT_TICKETS,
    { ttl: 60000, params: filters }
  );
  const tickets = Array.isArray(ticketsData) ? ticketsData : (ticketsData?.tickets || []);

  const handleCreateTicket = () => {
    setShowCreateModal(true);
  };

  const handleViewTicket = (ticket) => {
    setSelectedTicket(ticket);
    setShowDetailModal(true);
  };

  const handleFilterChange = (filterType, value) => {
    setFilters(prev => ({ ...prev, [filterType]: value }));
  };

  const columns = [
    {
      key: 'ticketId',
      label: 'Ticket ID',
      render: (row) => <span className="support-tickets__ticket-id">{row.ticketId?.slice(0, 8)}</span>
    },
    {
      key: 'title',
      label: 'Title',
      sortable: true
    },
    {
      key: 'status',
      label: 'Status',
      sortable: true,
      render: (row) => <StatusBadge status={row.status} type="ticket" />
    },
    {
      key: 'priority',
      label: 'Priority',
      sortable: true,
      render: (row) => <StatusBadge status={row.priority} type="priority" />
    },
    {
      key: 'category',
      label: 'Category',
      sortable: true
    },
    {
      key: 'assignedTo',
      label: 'Assigned To',
      render: (row) => row.assignedTo || <span className="support-tickets__unassigned">Unassigned</span>
    },
    {
      key: 'createdAt',
      label: 'Created',
      sortable: true,
      render: (row) => new Date(row.createdAt).toLocaleDateString()
    }
  ];



  if (loading) {
    return (
      <div className="support-tickets">
        <div className="support-tickets__header">
          <h1 className="support-tickets__title">Support Tickets</h1>
        </div>
        {[1, 2, 3, 4, 5].map((i) => (
          <Skeleton key={i} width="100%" height="80px" className="admin-skeleton-gap" />
        ))}
      </div>
    );
  }

  if (error) {
    return (
      <div className="support-tickets__error">
        <p>Error loading tickets: {error}</p>
        <button onClick={refetch} className="support-tickets__retry-button">Retry</button>
      </div>
    );
  }

  return (
    <div className="support-tickets">
      <div className="support-tickets__header">
        <h1 className="support-tickets__title">Support Tickets</h1>
        <button 
          onClick={handleCreateTicket}
          className="support-tickets__create-button"
        >
          Create Ticket
        </button>
      </div>

      <div className="support-tickets__filters">
        <SearchBar
          value={filters.search}
          onChange={(value) => handleFilterChange('search', value)}
          placeholder="Search tickets..."
        />
        
        <div className="support-tickets__filter-group">
          <select
            value={filters.status}
            onChange={(e) => handleFilterChange('status', e.target.value)}
            className="support-tickets__filter-select"
          >
            <option value="all">All Status</option>
            <option value="open">Open</option>
            <option value="in-progress">In Progress</option>
            <option value="resolved">Resolved</option>
            <option value="closed">Closed</option>
          </select>

          <select
            value={filters.priority}
            onChange={(e) => handleFilterChange('priority', e.target.value)}
            className="support-tickets__filter-select"
          >
            <option value="all">All Priority</option>
            <option value="low">Low</option>
            <option value="medium">Medium</option>
            <option value="high">High</option>
            <option value="critical">Critical</option>
          </select>

          <select
            value={filters.category}
            onChange={(e) => handleFilterChange('category', e.target.value)}
            className="support-tickets__filter-select"
          >
            <option value="all">All Categories</option>
            <option value="technical">Technical</option>
            <option value="billing">Billing</option>
            <option value="account">Account</option>
            <option value="feature-request">Feature Request</option>
            <option value="bug-report">Bug Report</option>
            <option value="other">Other</option>
          </select>

          <select
            value={filters.assignee}
            onChange={(e) => handleFilterChange('assignee', e.target.value)}
            className="support-tickets__filter-select"
          >
            <option value="all">All Assignees</option>
            <option value="unassigned">Unassigned</option>
            <option value={user.email}>My Tickets</option>
          </select>
        </div>
      </div>

      <DataTable
        data={tickets}
        columns={columns}
        rowActions={(row) => (
          <button onClick={() => handleViewTicket(row)} className="support-tickets__action--view">View Details</button>
        )}
        emptyMessage="No tickets found"
      />

      {showCreateModal && (
        <CreateTicketModal
          onClose={() => setShowCreateModal(false)}
          onSuccess={() => {
            setShowCreateModal(false);
            refetch();
          }}
          currentUser={user}
        />
      )}

      {showDetailModal && selectedTicket && (
        <TicketDetailModal
          ticket={selectedTicket}
          onClose={() => {
            setShowDetailModal(false);
            setSelectedTicket(null);
          }}
          onUpdate={() => {
            refetch();
          }}
          currentUser={user}
        />
      )}
    </div>
  );
};

const CreateTicketModal = ({ onClose, onSuccess, currentUser }) => {
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    priority: 'medium',
    category: 'technical',
    userId: ''
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!formData.title.trim()) {
      setError('Title is required');
      return;
    }
    if (!formData.description.trim()) {
      setError('Description is required');
      return;
    }

    setSubmitting(true);
    setError(null);

    try {
      const response = await fetch(API_ENDPOINTS.ADMIN.SUPPORT_TICKETS, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${await currentUser.getIdToken()}`
        },
        body: JSON.stringify({
          ...formData,
          createdBy: currentUser.email
        })
      });

      if (!response.ok) {
        throw new Error('Failed to create ticket');
      }

      onSuccess();
    } catch (err) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setError(null);
  };

  return (
    <Modal
      isOpen={true}
      onClose={onClose}
      title="Create Support Ticket"
    >
      <form onSubmit={handleSubmit} className="support-tickets__create-form">
        {error && (
          <div className="support-tickets__error-message">{error}</div>
        )}

        <div className="support-tickets__form-group">
          <label htmlFor="title" className="support-tickets__label">
            Title <span className="support-tickets__required">*</span>
          </label>
          <input
            id="title"
            type="text"
            value={formData.title}
            onChange={(e) => handleChange('title', e.target.value)}
            className="support-tickets__input"
            placeholder="Brief description of the issue"
            required
          />
        </div>

        <div className="support-tickets__form-group">
          <label htmlFor="description" className="support-tickets__label">
            Description <span className="support-tickets__required">*</span>
          </label>
          <textarea
            id="description"
            value={formData.description}
            onChange={(e) => handleChange('description', e.target.value)}
            className="support-tickets__textarea"
            placeholder="Detailed description of the issue"
            rows={6}
            required
          />
        </div>

        <div className="support-tickets__form-row">
          <div className="support-tickets__form-group">
            <label htmlFor="priority" className="support-tickets__label">
              Priority <span className="support-tickets__required">*</span>
            </label>
            <select
              id="priority"
              value={formData.priority}
              onChange={(e) => handleChange('priority', e.target.value)}
              className="support-tickets__select"
              required
            >
              <option value="low">Low</option>
              <option value="medium">Medium</option>
              <option value="high">High</option>
              <option value="critical">Critical</option>
            </select>
          </div>

          <div className="support-tickets__form-group">
            <label htmlFor="category" className="support-tickets__label">
              Category <span className="support-tickets__required">*</span>
            </label>
            <select
              id="category"
              value={formData.category}
              onChange={(e) => handleChange('category', e.target.value)}
              className="support-tickets__select"
              required
            >
              <option value="technical">Technical</option>
              <option value="billing">Billing</option>
              <option value="account">Account</option>
              <option value="feature-request">Feature Request</option>
              <option value="bug-report">Bug Report</option>
              <option value="other">Other</option>
            </select>
          </div>
        </div>

        <div className="support-tickets__form-group">
          <label htmlFor="userId" className="support-tickets__label">
            Related User ID (Optional)
          </label>
          <input
            id="userId"
            type="text"
            value={formData.userId}
            onChange={(e) => handleChange('userId', e.target.value)}
            className="support-tickets__input"
            placeholder="User ID if ticket is related to a specific user"
          />
        </div>

        <div className="support-tickets__form-actions">
          <button
            type="button"
            onClick={onClose}
            className="support-tickets__button support-tickets__button--secondary"
            disabled={submitting}
          >
            Cancel
          </button>
          <button
            type="submit"
            className="support-tickets__button support-tickets__button--primary"
            disabled={submitting}
          >
            {submitting ? 'Creating...' : 'Create Ticket'}
          </button>
        </div>
      </form>
    </Modal>
  );
};

const TicketDetailModal = ({ ticket, onClose, onUpdate, currentUser }) => {
  const [messages, setMessages] = useState(ticket.messages || []);
  const [newMessage, setNewMessage] = useState('');
  const [isInternal, setIsInternal] = useState(false);
  const [ticketData, setTicketData] = useState(ticket);
  const [submitting, setSubmitting] = useState(false);

  const handleAddMessage = async (e) => {
    e.preventDefault();
    
    if (!newMessage.trim()) return;

    setSubmitting(true);

    try {
      const response = await fetch(
        `${API_ENDPOINTS.ADMIN.SUPPORT_TICKETS}/${ticket.ticketId}/messages`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${await currentUser.getIdToken()}`
          },
          body: JSON.stringify({
            content: newMessage,
            isInternal,
            author: currentUser.email
          })
        }
      );

      if (!response.ok) {
        throw new Error('Failed to add message');
      }

      const updatedTicket = await response.json();
      setMessages(updatedTicket.messages);
      setNewMessage('');
      setIsInternal(false);
      onUpdate();
    } catch (err) {
      console.error('Error adding message:', err);
    } finally {
      setSubmitting(false);
    }
  };

  const handleStatusChange = async (newStatus) => {
    try {
      const response = await fetch(
        `${API_ENDPOINTS.ADMIN.SUPPORT_TICKETS}/${ticket.ticketId}`,
        {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${await currentUser.getIdToken()}`
          },
          body: JSON.stringify({ status: newStatus })
        }
      );

      if (!response.ok) {
        throw new Error('Failed to update status');
      }

      const updatedTicket = await response.json();
      setTicketData(updatedTicket);
      onUpdate();
    } catch (err) {
      console.error('Error updating status:', err);
    }
  };

  const handleAssign = async (assignee) => {
    try {
      const response = await fetch(
        `${API_ENDPOINTS.ADMIN.SUPPORT_TICKETS}/${ticket.ticketId}`,
        {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${await currentUser.getIdToken()}`
          },
          body: JSON.stringify({ assignedTo: assignee })
        }
      );

      if (!response.ok) {
        throw new Error('Failed to assign ticket');
      }

      const updatedTicket = await response.json();
      setTicketData(updatedTicket);
      onUpdate();
    } catch (err) {
      console.error('Error assigning ticket:', err);
    }
  };

  return (
    <Modal
      isOpen={true}
      onClose={onClose}
      title={`Ticket #${ticketData.ticketId.slice(0, 8)}`}
      size="large"
    >
      <div className="support-tickets__detail">
        <div className="support-tickets__detail-header">
          <div className="support-tickets__detail-info">
            <h2 className="support-tickets__detail-title">{ticketData.title}</h2>
            <div className="support-tickets__detail-meta">
              <StatusBadge status={ticketData.status} type="ticket" />
              <StatusBadge status={ticketData.priority} type="priority" />
              <span className="support-tickets__detail-category">{ticketData.category}</span>
            </div>
          </div>

          <div className="support-tickets__detail-actions">
            <select
              value={ticketData.status}
              onChange={(e) => handleStatusChange(e.target.value)}
              className="support-tickets__status-select"
            >
              <option value="open">Open</option>
              <option value="in-progress">In Progress</option>
              <option value="resolved">Resolved</option>
              <option value="closed">Closed</option>
            </select>

            <button
              onClick={() => handleAssign(currentUser.email)}
              className="support-tickets__assign-button"
              disabled={ticketData.assignedTo === currentUser.email}
            >
              {ticketData.assignedTo === currentUser.email ? 'Assigned to Me' : 'Assign to Me'}
            </button>
          </div>
        </div>

        <div className="support-tickets__detail-body">
          <div className="support-tickets__detail-description">
            <h3>Description</h3>
            <p>{ticketData.description}</p>
          </div>

          <div className="support-tickets__detail-metadata">
            <div className="support-tickets__metadata-item">
              <span className="support-tickets__metadata-label">Created By:</span>
              <span className="support-tickets__metadata-value">{ticketData.createdBy}</span>
            </div>
            <div className="support-tickets__metadata-item">
              <span className="support-tickets__metadata-label">Created At:</span>
              <span className="support-tickets__metadata-value">
                {new Date(ticketData.createdAt).toLocaleString()}
              </span>
            </div>
            <div className="support-tickets__metadata-item">
              <span className="support-tickets__metadata-label">Assigned To:</span>
              <span className="support-tickets__metadata-value">
                {ticketData.assignedTo || 'Unassigned'}
              </span>
            </div>
            {ticketData.userId && (
              <div className="support-tickets__metadata-item">
                <span className="support-tickets__metadata-label">Related User:</span>
                <span className="support-tickets__metadata-value">{ticketData.userId}</span>
              </div>
            )}
          </div>

          <div className="support-tickets__messages">
            <h3>Messages</h3>
            <div className="support-tickets__message-list">
              {messages.length === 0 ? (
                <p className="support-tickets__no-messages">No messages yet</p>
              ) : (
                messages.map((message, index) => (
                  <div
                    key={index}
                    className={`support-tickets__message ${
                      message.isInternal ? 'support-tickets__message--internal' : ''
                    }`}
                  >
                    <div className="support-tickets__message-header">
                      <span className="support-tickets__message-author">{message.author}</span>
                      <span className="support-tickets__message-timestamp">
                        {new Date(message.timestamp).toLocaleString()}
                      </span>
                      {message.isInternal && (
                        <span className="support-tickets__message-badge">Internal Note</span>
                      )}
                    </div>
                    <div className="support-tickets__message-content">{message.content}</div>
                  </div>
                ))
              )}
            </div>

            <form onSubmit={handleAddMessage} className="support-tickets__message-form">
              <textarea
                value={newMessage}
                onChange={(e) => setNewMessage(e.target.value)}
                className="support-tickets__message-input"
                placeholder="Add a message or internal note..."
                rows={3}
              />
              <div className="support-tickets__message-actions">
                <label className="support-tickets__internal-checkbox">
                  <input
                    type="checkbox"
                    checked={isInternal}
                    onChange={(e) => setIsInternal(e.target.checked)}
                  />
                  Internal Note (not visible to user)
                </label>
                <button
                  type="submit"
                  className="support-tickets__button support-tickets__button--primary"
                  disabled={submitting || !newMessage.trim()}
                >
                  {submitting ? 'Sending...' : 'Send Message'}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default SupportTickets;
